import { BaseAPI } from 'API/BaseApi';
import { eAPITags } from 'API/eAPITags';
import { IEscrowBankAccountResponse } from 'types/accounting/response/IEscrowBankAccountResponse';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { ICreateInternalTransferDisbursement } from 'types/order/escrow/internalTransferDisbursements/ICreateInternalTransferDisbursement';
import { IInternalTransferDisbursementFormData } from 'types/order/escrow/internalTransferDisbursements/IInternalTransferDisbursementFormData';
import { IUpdateInternalTransferDisbursement } from 'types/order/escrow/internalTransferDisbursements/IUpdateInternalTransferDisbursement';
import { emptyFieldsToUndefined } from './EscrowBaseAPI';
import { IInternalTransferResponse } from 'types/order/escrow/response/IInternalTransferResponse';
import { IInternalTransferDisbursementVoidRequest } from 'types/accounting/IInternalTransferVoidRequest';

const InternalTransferDisbursementAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    getInternalTransferDisbursement: build.query<IInternalTransferDisbursementFormData, { internalTransferId: string }>(
      {
        query: ({ internalTransferId }) => ({
          url: `/timber/api/internal/escrow/expected_internal_transfers/${internalTransferId}`,
          method: 'GET',
        }),
        transformResponse: ({ destinationAccount, ...internalTransfer }: IInternalTransferResponse) => ({
          ...internalTransfer,
          type: eDisbursementType.InternalTransfer,
          destinationAccountId: destinationAccount?.id,
        }),
        providesTags: (result, error, { internalTransferId }) => [
          { type: eAPITags.InternalTransferDisbursement, id: internalTransferId },
        ],
      }
    ),
    createInternalTransferDisbursement: build.mutation<void, ICreateInternalTransferDisbursement>({
      query: (data) => ({
        url: `/timber/api/internal/escrow/expected_internal_transfers`,
        method: 'POST',
        body: emptyFieldsToUndefined(data),
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
      ],
    }),
    updateInternalTransferDisbursement: build.mutation<void, IUpdateInternalTransferDisbursement>({
      query: ({ orderId, ...body }) => ({
        url: `/timber/api/internal/escrow/expected_internal_transfers/${body.id}`,
        method: 'PATCH',
        body: emptyFieldsToUndefined(body),
      }),
      invalidatesTags: (result, error, { orderId, id }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.InternalTransferDisbursement, id },
      ],
    }),
    deleteInternalTransferDisbursement: build.mutation<void, { internalTransferId: string; orderId: string }>({
      query: ({ internalTransferId }) => ({
        url: `/timber/api/internal/escrow/expected_internal_transfers/${internalTransferId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId, internalTransferId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.InternalTransferDisbursement, id: internalTransferId },
      ],
    }),
    voidInternalDisbursement: build.mutation<
      void,
      { orderId: string; internalTransfer: IInternalTransferDisbursementVoidRequest }
    >({
      query: ({ internalTransfer }) => ({
        url: `/timber/api/internal/escrow/expected_internal_transfers/void`,
        method: 'POST',
        body: internalTransfer,
      }),
      invalidatesTags: (result, error, { orderId, internalTransfer }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.InternalTransferDisbursement, id: internalTransfer.id },
      ],
    }),
    getDestinationAccountsForState: build.query<IEscrowBankAccountResponse[], string>({
      query: (orderId) => ({
        url: `/timber/api/internal/escrow/bank_accounts/${orderId}/deposit_accounts`,
        method: 'GET',
      }),
      providesTags: (result, error, orderId) => [{ type: eAPITags.OrderData, id: orderId }],
    }),
    getDestinationAccount: build.query<IEscrowBankAccountResponse, { category: string; state: string }>({
      query: ({ category, state }) => ({
        url: `/timber/api/internal/escrow/bank_accounts/findBy/transactionCategory/${category}/state/${state}`,
        method: 'GET',
      }),
    }),
    sendInternalTransferDisbursement: build.mutation<void, { orderId: string; internalTransferId: string }>({
      query: ({ internalTransferId }) => ({
        url: `/timber/api/internal/escrow/expected_internal_transfers/${internalTransferId}/send`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { orderId, internalTransferId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.InternalTransferDisbursement, id: internalTransferId },
      ],
    }),
  }),
});

export const {
  useGetInternalTransferDisbursementQuery,
  useCreateInternalTransferDisbursementMutation,
  useDeleteInternalTransferDisbursementMutation,
  useUpdateInternalTransferDisbursementMutation,
  useGetDestinationAccountsForStateQuery,
  useSendInternalTransferDisbursementMutation,
  useGetDestinationAccountQuery,
  useVoidInternalDisbursementMutation,
} = InternalTransferDisbursementAPI;
